import { useState } from 'react';
import type { WoningData, WoningType, Bouwjaar } from '@shared/types';

interface Props {
  state: WoningData;
  onChange: (data: WoningData) => void;
  onNext: () => void;
  onBack: () => void;
  returnToSamenvatting?: boolean;
  onReturnToSamenvatting?: () => void;
}

export default function StapWoning({ state, onChange, onNext, onBack, returnToSamenvatting, onReturnToSamenvatting }: Props) {
  const [errors, setErrors] = useState<Partial<Record<keyof WoningData, string>>>({});

  function validate(): boolean {
    const e: Partial<Record<keyof WoningData, string>> = {};
    if (!state.type) e.type = 'Selecteer het type woning.';
    if (state.type === 'anders' && !state.typeAnders?.trim())
      e.typeAnders = 'Vul in wat voor type woning het is.';
    if (!state.oppervlakte || state.oppervlakte < 30 || state.oppervlakte > 500)
      e.oppervlakte = 'Vul een geldig woonoppervlakte in (30–500 m²).';
    if (!state.bouwjaar) e.bouwjaar = 'Selecteer het bouwjaar.';
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  function handleSubmit(ev: React.FormEvent) {
    ev.preventDefault();
    if (validate()) onNext();
  }

  return (
    <form className="lc-stap lc-stap--woning" onSubmit={handleSubmit} noValidate>
      <h2 className="lc-stap__titel">Woninggegevens</h2>
      <p className="lc-stap__beschrijving">
        We gebruiken deze gegevens om de belastingberekening te verbeteren en je netbeheerder te bepalen.
      </p>

      {/* Type woning */}
      <fieldset className="lc-veld lc-veld--radio-groep">
        <legend className="lc-veld__label">
          Type woning <span aria-hidden="true">*</span>
        </legend>
        {(
          [
            { waarde: 'vrijstaand', label: 'Vrijstaand huis' },
            { waarde: 'twee_onder_een_kap', label: '2-onder-1-kap' },
            { waarde: 'rijtjeshuis', label: 'Rijtjeshuis / tussenwoning' },
            { waarde: 'hoekwoning', label: 'Hoekwoning' },
            { waarde: 'appartement', label: 'Appartement' },
            { waarde: 'anders', label: 'Anders' },
          ] as { waarde: WoningType; label: string }[]
        ).map(({ waarde, label }) => (
          <label key={waarde} className="lc-radio">
            <input
              type="radio"
              name="woningType"
              value={waarde}
              checked={state.type === waarde}
              onChange={() => onChange({ ...state, type: waarde, typeAnders: waarde === 'anders' ? (state.typeAnders ?? '') : null })}
            />
            <span>{label}</span>
          </label>
        ))}
        {errors.type && <p className="lc-veld__fout" role="alert">{errors.type}</p>}

        {/* A4: free text field when "Anders" is selected */}
        {state.type === 'anders' && (
          <div className="lc-veld lc-veld--anders">
            <label className="lc-veld__label" htmlFor="lc-woning-anders">
              Omschrijf het type woning <span aria-hidden="true">*</span>
            </label>
            <input
              id="lc-woning-anders"
              type="text"
              className={`lc-veld__input${errors.typeAnders ? ' lc-veld__input--fout' : ''}`}
              placeholder="bijv. woonboerderij, chalet, tiny house…"
              value={state.typeAnders ?? ''}
              onChange={(e) => onChange({ ...state, typeAnders: e.target.value })}
              autoComplete="off"
            />
            {errors.typeAnders && (
              <p className="lc-veld__fout" role="alert">{errors.typeAnders}</p>
            )}
          </div>
        )}
      </fieldset>

      {/* Oppervlakte */}
      <div className="lc-veld">
        <label className="lc-veld__label" htmlFor="lc-oppervlakte">
          Woonoppervlakte (m²){' '}
          <span aria-hidden="true">*</span>
          <span className="lc-tooltip" title="Gevonden in je koopakte of op Funda">
            {' '}ⓘ
          </span>
        </label>
        <input
          id="lc-oppervlakte"
          className={`lc-veld__input lc-veld__input--kort${errors.oppervlakte ? ' lc-veld__input--fout' : ''}`}
          type="number"
          min={30}
          max={500}
          placeholder="bijv. 120"
          value={state.oppervlakte ?? ''}
          onChange={(e) => onChange({ ...state, oppervlakte: parseInt(e.target.value) || null })}
        />
        {errors.oppervlakte && (
          <p className="lc-veld__fout" role="alert">{errors.oppervlakte}</p>
        )}
      </div>

      {/* Bouwjaar */}
      <fieldset className="lc-veld lc-veld--radio-groep">
        <legend className="lc-veld__label">
          Bouwjaar <span aria-hidden="true">*</span>
        </legend>
        {(
          [
            { waarde: 'voor_1970', label: 'Voor 1970' },
            { waarde: '1970_1990', label: '1970–1990' },
            { waarde: '1991_2010', label: '1991–2010' },
            { waarde: 'na_2010', label: 'Na 2010' },
          ] as { waarde: Bouwjaar; label: string }[]
        ).map(({ waarde, label }) => (
          <label key={waarde} className="lc-radio">
            <input
              type="radio"
              name="bouwjaar"
              value={waarde}
              checked={state.bouwjaar === waarde}
              onChange={() => onChange({ ...state, bouwjaar: waarde })}
            />
            <span>{label}</span>
          </label>
        ))}
        {errors.bouwjaar && (
          <p className="lc-veld__fout" role="alert">{errors.bouwjaar}</p>
        )}
      </fieldset>

      <div className="lc-stap__acties">
        {returnToSamenvatting && (
          <button type="button" className="lc-knop lc-knop--terug-samenvatting" onClick={onReturnToSamenvatting}>
            ← Terug naar samenvatting
          </button>
        )}
        <button type="button" className="lc-knop lc-knop--terug" onClick={onBack}>
          ← Terug
        </button>
        <button type="submit" className="lc-knop lc-knop--primair">
          {returnToSamenvatting ? 'Opslaan & terug →' : 'Volgende →'}
        </button>
      </div>
    </form>
  );
}
